import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {RootState} from '@src/redux/store';
import {BASE_URL} from '@src/helpers/constants';
import {retrieveItem} from '@src/helpers/functions';
import * as TYPES from '@src/redux/types';

const GRAPHQL_QUERY = `
  query getReportLevelOptionList($parentOrganizationId: String) {
    getReportLevelOptionList(parentOrganizationId: $parentOrganizationId) {
      levelId
      levelName
      items {
        id
        name
        levelId
        levelName
      }
      error {
        message
        statusCode
      }
    }
  }
`;

export type GetReportLevelOptionListParams = {
  parentOrganizationId?: string;
};

const initialState: TYPES.ReportLevelOptionListState = {
  result: null,
  error: null,
  loading: false,
};

export const getReportLevelOptionListRequest = createAsyncThunk<
  TYPES.ReportLevelOptionListResponse,
  GetReportLevelOptionListParams,
  {extra: ApolloClient<NormalizedCacheObject>}
>('report/get_level_option_list', async (params) => {
  const sessionId = retrieveItem('sessionId') ?? '';
  try {
    const response = await fetch(BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        sessionId: sessionId,
      },
      body: JSON.stringify({
        operationName: 'getReportLevelOptionList',
        query: GRAPHQL_QUERY,
        variables: params ?? {},
      }),
    });
    const json = await response.json();
    if (json?.errors?.length) {
      const first = json.errors[0];
      return Promise.reject({
        message: first.message || 'Failed to load levels',
        statusCode: String(response.status),
      });
    }
    return json?.data
      ?.getReportLevelOptionList as TYPES.ReportLevelOptionListResponse;
  } catch (error) {
    const err = error as {message?: string; statusCode?: string};
    return Promise.reject({
      message: err.message || 'An error occurred',
      statusCode: err.statusCode || '500',
    });
  }
});

const getReportLevelOptionListSlice = createSlice({
  name: 'get_report_level_option_list',
  initialState: initialState,
  reducers: {
    getReportLevelOptionListClear: state => {
      state.result = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getReportLevelOptionListRequest.pending, state => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getReportLevelOptionListRequest.fulfilled, (state, action) => {
        state.result = action.payload;
        state.loading = false;
      })
      .addCase(getReportLevelOptionListRequest.rejected, (state, action) => {
        state.error =
          (action.payload as TYPES.Error) ||
          {message: 'Failed to fetch', statusCode: '500'};
        state.loading = false;
      });
  },
});

export const {getReportLevelOptionListClear} =
  getReportLevelOptionListSlice.actions;

export const getReportLevelOptionList = createSelector(
  (state: RootState) => state.report.getReportLevelOptionList.result,
  (state: RootState) => state.report.getReportLevelOptionList.error,
  (state: RootState) => state.report.getReportLevelOptionList.loading,
  (result, error, loading) => ({result, error, loading}),
);

export default getReportLevelOptionListSlice.reducer;
